<script setup lang="ts">
import { inject, computed } from 'vue'
import type { Props } from './types'
import { investmentProjectKey } from '@/symbols/listing'
import AppMapStatic from '@/components/App/Map/Static/index.vue'
import { whatsappListingNumbers } from '@/config/whatsappListingNumbers'
import type { LocaleCode } from '@/config/locale-domains'
import './_index.scss'

const props = defineProps<Props>()

const listing = inject(investmentProjectKey)
const { $whatsappNumbers } = useNuxtApp()
const { locale } = useI18n()
const { t } = useI18n()
const route = useRoute()
const slug = route.params.slug as string

const whatsappUrl = computed(() => {
  const number = whatsappListingNumbers[slug] ?? ($whatsappNumbers as Record<LocaleCode, string>)[locale.value as LocaleCode]
  const text = encodeURIComponent(`Hola me gustaría más información sobre ${props.listing.name}`)
  return `https://wa.me/${number}?text=${text}`
})

</script>

<script lang="ts">
export default {
  name: 'PdInPersonVisit'
}
</script>

<template>
  <div class="pd-in-person-visit page-pd-city-slug__section page-pd-city-slug__section--padded py-48">
    <div class="pd-in-person-visit__container flex flex-col md:flex-row mt-16 md:mt-24 outline-none">
      <div class="pd-in-person-visit__description text-white bg-carbon-800 rounded-t-16 md:rounded-l-16 md:rounded-r-0 px-24 py-32 md:p-56 flex flex-col justify-between">
        <div>
          <div class="pd-in-person-visit__description__title flex justify-between md:pr-0">
            <p
              v-sanitize="t('description.text1')"
              class="text-22 md:text-32 font-bold" />

            <img
              :src="t('imgUrl')"
              alt="ícono de forma"
              class="w-40 h-40">
          </div>

          <div>
            <p class="pd-in-person-visit__description__testimonial text-14 md:text-16 pr-16 pt-10">
              {{ t('description.text2') }}
              <span class="font-bold">
                {{ t('description.text3') }}
              </span>
            </p>
          </div>
        </div>

        <div
          class="pd-in-person-visit__contact-form flex items-center gap-10">
          <a
            id="pd-in-person-button"
            :href="whatsappUrl"
            data-lh-id="pd-in-person-submit"
            target="_blank"
            rel="noopener noreferrer"
            class="w-full flex items-center justify-center py-12 px-24 border-2 border-white text-white rounded-8 font-medium hover:bg-white hover:text-carbon-800 transition-colors duration-200">
            {{ t('contactForm.ctaText') }}
          </a>
        </div>
      </div>

      <div
        id="pd-map-section"
        class="pd-in-person-visit__map border-carbon-800 border-4 relative rounded-b-16 md:rounded-r-16 md:rounded-l-0">
        <AppMapStatic
          :id="`pdp-in-person-visit-static-map-button-${listing?.code}`"
          class="flex-1 is-form-visible"
          :lat="lat"
          :lng="lng"
          :location-name="locationName"
          :is-mobile="isMobile"
          button-styles="w-full h-full rounded-b-16 md:rounded-b-0 md:rounded-r-16"
          img-styles="w-full h-full object-cover"
          @open-gallery="openGallery" />
      </div>
    </div>
  </div>
</template>

<i18n src="./i18n.json" />
